// src/components/Weather/WeatherDashboardSummary.js
// src/components/Weather/WeatherDashboardSummary.js
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../../UserContext";
import { getWeatherData } from "./weatherService"; // UPDATED to use the main service
import { WiHumidity } from "react-icons/wi";
import { FaWind, FaChevronLeft, FaChevronRight } from "react-icons/fa";

// NEW: Helper functions to parse location and calculate the center
const parseCustomLocationString = (locationString) => {
  if (!locationString || typeof locationString !== "string") return [];
  const pairs = locationString.split(/(?<=\d)-/g);
  return pairs
    .map((pair) => pair.split(",").map(Number))
    .filter(
      (coords) =>
        Array.isArray(coords) &&
        coords.length === 2 &&
        !isNaN(coords[0]) &&
        !isNaN(coords[1])
    );
};

const calculateCentroid = (boundaries) => {
  if (!boundaries || boundaries.length === 0) return { lat: 0, lon: 0 };
  const center = boundaries.reduce(
    (acc, curr) => [
      acc[0] + curr[0] / boundaries.length,
      acc[1] + curr[1] / boundaries.length,
    ],
    [0, 0]
  );
  return { lat: center[0], lon: center[1] };
};
const WeatherDashboardSummary = () => {
  const { t, i18n } = useTranslation();
  const { userData, token } = useContext(UserContext);
  const navigate = useNavigate();
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [primaryField, setPrimaryField] = useState(null);

  // Step 1: Fetch the user's fields to find a location for the weather
  useEffect(() => {
    if (!userData?.Farm?.id || !token) {
      setLoading(false);
      return;
    }

    const fetchPrimaryField = async () => {
      try {
        const farmId = userData.Farm.id;
        const response = await axios.get(`api/field/findByFarmId/${farmId}`, {
          headers: { Authorization: token },
        });

        if (response.data.items && response.data.items.length > 0) {
          const firstField = response.data.items[0];
          firstField.boundaries = parseCustomLocationString(
            firstField.location
          );
          console.log("first field", firstField);
          setPrimaryField(firstField);
        } else {
          setLoading(false); // No fields, stop loading
        }
      } catch (error) {
        console.error(
          "Failed to fetch primary field for weather summary",
          error
        );
        setLoading(false);
      }
    };

    fetchPrimaryField();
  }, [userData, token]);

  // Step 2: Once we have a field, fetch its weather
  useEffect(() => {
    if (!primaryField) return;

    const fetchWeatherSummary = async () => {
      const { lat, lon } = calculateCentroid(primaryField.boundaries);
      try {
        const response = await getWeatherData(lat, lon, i18n.language);
        console.log("weather data",response.data)
        setWeatherData(response.data);
      } catch (error) {
        console.error("Failed to load dashboard weather summary", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWeatherSummary();
  }, [primaryField, i18n.language]);

  const handleNavigate = () => {
    navigate("/weather");
  };

  if (loading || !weatherData) {
    return (
      <div className="weather-summary-card-placeholder">
        {t("Loading Weather...")}
      </div>
    );
  }

  const current = weatherData.current;
  const tomorrow = weatherData.daily[1];
  const isRTL = i18n.dir() === "rtl";

  return i18n.language === "ar" ? (
    <div className="weather-summary-card" onClick={handleNavigate}>
      <div className="forecast-snippet">
        <div className="forecast-details">
          <span className="forecast-day">{t("Tomorrow")}</span>
          <div className="forecast-icon-dot"></div>
          <span className="forecast-temp">
            {Math.round(tomorrow.temp.max)}°
          </span>
        </div>
        <FaChevronLeft className="forecast-arrow" />
      </div>

      <div className="main-weather-details">
        <p className="main-temp">{current.temp.toFixed(2)}°C</p>
        <p className="main-desc">{current.weather[0].description}</p>
        <div className="secondary-info">
          <span>
            <FaWind /> {current.wind_speed} {t("m/s")}
          </span>
          <span>
            <WiHumidity /> {current.humidity}%
          </span>
        </div>
        <p className="details-link">{t("Click for details")}</p>
      </div>

      <div className="main-weather-icon">
        <img
          src={`https://openweathermap.org/img/wn/${current.weather[0].icon}@2x.png`}
          alt=""
        />
      </div>
    </div>
  ) : (
    <div className="weather-summary-card" onClick={handleNavigate}>
      <div className="main-weather-icon">
        <img
          src={`https://openweathermap.org/img/wn/${current.weather[0].icon}@2x.png`}
          alt=""
        />
      </div>

      <div className="main-weather-details">
        <p className="main-temp">{current.temp.toFixed(2)}°C</p>
        <p className="main-desc">{current.weather[0].description}</p>
        <div className="secondary-info">
          <span>
            <FaWind /> {current.wind_speed} {t("m/s")}
          </span>
          <span>
            <WiHumidity /> {current.humidity}%
          </span>
        </div>
        <p className="details-link">{t("Click for details")}</p>
      </div>

      <div className="forecast-snippet">
        {isRTL && <FaChevronLeft className="forecast-arrow" />}
        <div className="forecast-details">
          <span className="forecast-day">{t("Tomorrow")}</span>
          <div className="forecast-icon-dot"></div>
          <span className="forecast-temp">
            {Math.round(tomorrow.temp.max)}°
          </span>
        </div>
        {!isRTL && <FaChevronRight className="forecast-arrow" />}
      </div>
    </div>
  );
};

export default WeatherDashboardSummary;
